import { WidgetControlType } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating the monotonicity feature.
 *
 * Shows a quadratic function with colored background regions indicating
 * where the function is increasing (green) or decreasing (red).
 *
 * For f(x) = a(x-h)² + k with a > 0:
 * - Decreasing on (-∞, h) where f'(x) = 2a(x-h) < 0
 * - Increasing on (h, +∞) where f'(x) = 2a(x-h) > 0
 * - Vertex (minimum) at x = h
 *
 * For a < 0: directions are reversed (increasing then decreasing).
 */
export const TEST_CALCULUS_PRESET: GraphpadWidgetConfig = {
  id: 'test-calculus',
  type: 'graphpad',
  params: {
    f: {
      type: 'quadratic',
      params: { a: 1, h: 0, k: -4 },
    },
    'f-monotonicity': {
      type: 'monotonicity',
      params: { of: 'f' },
    },
    'f-extrema': {
      type: 'extrema',
      params: { of: 'f' },
    },
    derivative: {
      type: 'derivative',
      params: { of: 'f' },
    },
    'derivative-roots': {
      type: 'roots',
      params: { of: 'derivative' },
    },
  },
  display: {
    coordinateSystem: {
      boundingbox: { xStart: -6, xEnd: 6, yStart: -6, yEnd: 8 },
      grid: { type: 'lines', visible: true },
      axes: true,
    },
    elements: {
      'f-monotonicity': {
        polygon: { visible: true, fillOpacity: 0.12 },
        curve: { visible: true, strokeWidth: 4 },
        subparts: {
          increasing: {
            polygon: { fillColor: 'green' },
            curve: { color: 'green' },
          },
          decreasing: {
            polygon: { fillColor: 'red' },
            curve: { color: 'red' },
          },
        },
      },
      'f-extrema': {
        point: {
          color: 'amber',
          label: { contentVariant: 'name+coord', position: 'top' },
        },
      },
      'derivative-roots': {
        point: {
          color: 'sky',
          label: { contentVariant: 'name+coord', position: 'top' },
        },
      },
      derivative: {
        curve: { color: 'sky' },
      },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-a',
        type: WidgetControlType.Slider,
        label: 'a',
        param: 'f.a',
        range: [-2, 2, 0.1],
        defaultValue: 1,
        editable: true,
        color: 'amber',
      },
      {
        id: 'slider-h',
        type: WidgetControlType.Slider,
        label: 'h',
        param: 'f.h',
        range: [-5, 5, 0.5],
        defaultValue: 0,
        editable: true,
        color: 'amber',
      },
      {
        id: 'slider-k',
        type: WidgetControlType.Slider,
        label: 'k',
        param: 'f.k',
        range: [-5, 5, 0.5],
        defaultValue: -4,
        editable: true,
        color: 'amber',
      },
    ],
  },
};
